import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Import pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import AccountPicker from './pages/AccountPicker';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import TripDetails from './pages/TripDetails';
import TripsManagement from './pages/TripsManagement';
import ParticipantsManagement from './pages/ParticipantsManagement';
import InventorySystem from './pages/InventorySystem';
import TransportBags from './pages/TransportBags';
import CountSession from './pages/CountSession';
import HotelManagement from './pages/HotelManagement';
import WaiverPage from './pages/WaiverPage';
import ReminderSettingsPage from './pages/ReminderSettingsPage';
import LicenseUploadPage from './pages/LicenseUploadPage';
import LicenseManagementPage from './pages/LicenseManagementPage';
import MissionInfoPage from './pages/MissionInfoPage';
import ReturningParticipantPage from './pages/ReturningParticipantPage';
import SignupPage from './pages/SignupPage';
import PermissionsPage from './pages/PermissionsPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import SiteSettingsPage from './pages/SiteSettingsPage';
import QAPage from './pages/QAPage';
import AdminQAPage from './pages/AdminQAPage';
import MyTripsRedirect from './pages/MyTripsRedirect';
import FlightHotelPage from './pages/FlightHotelPage';
import TripInterestPage from './pages/TripInterestPage';
import FoodFunPage from './pages/FoodFunPage';
import MaintenancePage from './pages/MaintenancePage';
import HowToPage from './pages/HowToPage';
import AssignmentsPage from './pages/AssignmentsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DirectoryPage from './pages/DirectoryPage';
import AuditLogsPage from './pages/AuditLogsPage';
import { SiteSettingsProvider } from './SiteSettingsContext';

import './App.css';

import { api, clearSessionToken } from './api';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    setUser(response.data);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearSessionToken();
    setUser(null);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'partial_admin' || user?.permanent_roles?.includes('admin');

  // Partial admins see the admin sidebar but individual items may be greyed/hidden
  const isFullAdmin = user?.role === 'admin' || user?.permanent_roles?.includes('admin');

  // Check if user has permission for a specific section
  const hasPermission = (section, level = 'view') => {
    if (isAdmin) return true;
    return user?.app_permissions?.[section]?.[level] === true;
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      register,
      loginWithGoogle,
      logout,
      isAdmin,
      isFullAdmin,
      hasPermission,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Registration disabled — invite only */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected routes */}
      <Route path="/account-picker" element={
        <ProtectedRoute>
          <AccountPicker />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/trips/:tripId" element={
        <ProtectedRoute>
          <TripDetails />
        </ProtectedRoute>
      } />
      <Route path="/admin/assignments" element={
        <ProtectedRoute adminOnly>
          <AssignmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/flight-hotel" element={
        <ProtectedRoute>
          <FlightHotelPage />
        </ProtectedRoute>
      } />
      {/* Old /tasks route redirects to dashboard */}
      <Route path="/tasks" element={<Navigate to="/dashboard" replace />} />
      <Route path="/waiver" element={
        <ProtectedRoute>
          <WaiverPage />
        </ProtectedRoute>
      } />
      <Route path="/trip-interest" element={
        <ProtectedRoute>
          <TripInterestPage />
        </ProtectedRoute>
      } />
      <Route path="/food-fun" element={
        <ProtectedRoute>
          <FoodFunPage />
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/trips" element={
        <ProtectedRoute adminOnly>
          <TripsManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/participants" element={
        <ProtectedRoute adminOnly>
          <ParticipantsManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/hotels" element={
        <ProtectedRoute adminOnly>
          <HotelManagement />
        </ProtectedRoute>
      } />
      {/* Old routes redirect to new combined Flight & Hotel page */}
      <Route path="/trips/:tripId/flights" element={<Navigate to="/flight-hotel" replace />} />
      <Route path="/trips/:tripId/hotel-preferences" element={<Navigate to="/flight-hotel" replace />} />
      {/* Old room assignment route redirects to hotels page */}
      <Route path="/trips/:tripId/rooms" element={
        <ProtectedRoute adminOnly>
          <HotelManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/reminders" element={
        <ProtectedRoute adminOnly>
          <ReminderSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/inventory-system" element={
        <ProtectedRoute adminOnly>
          <InventorySystem />
        </ProtectedRoute>
      } />
      <Route path="/admin/bags" element={
        <ProtectedRoute adminOnly>
          <TransportBags />
        </ProtectedRoute>
      } />
      <Route path="/admin/count-session" element={
        <ProtectedRoute adminOnly>
          <CountSession />
        </ProtectedRoute>
      } />
      <Route path="/licenses" element={
        <ProtectedRoute>
          <LicenseUploadPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/licenses" element={
        <ProtectedRoute adminOnly>
          <LicenseManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/info" element={<MissionInfoPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/returning" element={<ReturningParticipantPage />} />
      <Route path="/admin/permissions" element={
        <ProtectedRoute adminOnly>
          <PermissionsPage />
        </ProtectedRoute>
      } />
      <Route path="/accept-invite/:token" element={<InviteAcceptPage />} />
      <Route path="/admin/site-settings" element={
        <ProtectedRoute adminOnly>
          <SiteSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/my-trips" element={
        <ProtectedRoute>
          <MyTripsRedirect />
        </ProtectedRoute>
      } />
      <Route path="/ask" element={
        <ProtectedRoute>
          <QAPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/qa" element={
        <ProtectedRoute adminOnly>
          <AdminQAPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/directory" element={
        <ProtectedRoute adminOnly>
          <DirectoryPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute adminOnly>
          <AuditLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/maintenance" element={
        <ProtectedRoute adminOnly>
          <MaintenancePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/how-to" element={
        <ProtectedRoute>
          <HowToPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SiteSettingsProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </SiteSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
